import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { getCurrentMonthlyOffer } from "@/data/monthlyOffers";
import { trackEvent } from "@/utils/tracking";
import { trackBookingAttribution } from "@/utils/attribution";

const STORAGE_KEY = "announcementBarDismissedAt";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const BAR_HEIGHT_PX = 44;

function isDismissed() {
  try {
    const ts = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_MS;
  } catch {
    return false;
  }
}

export default function AnnouncementBar() {
  // Start hidden to prevent flash before we know dismiss state / SSR mismatch.
  const [visible, setVisible] = useState(false);
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    if (isDismissed()) return;
    setOffer(getCurrentMonthlyOffer());
    setVisible(true);
    document.documentElement.style.setProperty("--announcement-h", `${BAR_HEIGHT_PX}px`);
    document.body.classList.add("has-announcement");
    trackEvent("announcement_bar_view", { month: new Date().getMonth() });
    return () => {
      document.documentElement.style.setProperty("--announcement-h", "0px");
      document.body.classList.remove("has-announcement");
    };
  }, []);

  if (!visible || !offer) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* storage disabled — dismiss for this session only */
    }
    setVisible(false);
    document.documentElement.style.setProperty("--announcement-h", "0px");
    document.body.classList.remove("has-announcement");
    trackEvent("announcement_bar_dismiss", { month: new Date().getMonth() });
  };

  const handleCtaClick = () => {
    trackEvent("announcement_bar_cta_click", {
      month: new Date().getMonth(),
      cta: offer.cta,
    });
    trackBookingAttribution("announcement_bar", "click", {
      month: new Date().getMonth(),
      cta: offer.cta,
    });
  };

  return (
    <div
      data-testid="announcement-bar"
      role="region"
      aria-label="Seasonal promotion"
      className={`fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r ${offer.accent} text-white shadow-[0_2px_12px_rgba(0,0,0,0.35)]`}
      style={{ height: `${BAR_HEIGHT_PX}px` }}
    >
      <div className="max-w-7xl mx-auto h-full px-4 md:px-12 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span aria-hidden="true" className="text-sm md:text-base flex-shrink-0">
            {offer.icon}
          </span>
          <p
            data-testid="announcement-bar-message"
            className="text-[12px] md:text-[13px] font-medium leading-tight truncate md:whitespace-normal"
          >
            {offer.message}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Link
            to="/contact"
            onClick={handleCtaClick}
            data-testid="announcement-bar-cta"
            className="hidden sm:inline-flex items-center min-h-[32px] bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white text-[11px] md:text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors border border-white/20"
          >
            {offer.cta} →
          </Link>
          <Link
            to="/contact"
            onClick={handleCtaClick}
            data-testid="announcement-bar-cta-mobile"
            aria-label={offer.cta}
            className="sm:hidden inline-flex items-center justify-center min-w-[44px] min-h-[44px] bg-white/15 hover:bg-white/25 text-white text-[15px] font-semibold px-3 rounded-full transition-colors border border-white/20"
          >
            →
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="announcement-bar-dismiss"
            aria-label="Dismiss announcement"
            className="ml-1 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full hover:bg-white/15 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
